import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import CloseFilledIcon from '@mui/icons-material/Close'
import ListItemIcon from '@mui/material/ListItemIcon'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import IconButton from '@mui/material/IconButton'
import { GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid/models'
import { TimeField } from '@mui/x-date-pickers/TimeField'
import { Dayjs } from 'dayjs'
import { Work } from '@/javascript/domain/entities/work'
import SelectionDataGrid from '@/javascript/presentation/components/data-grid/selection-data-grid'
import { openWorkDetails } from '@/javascript/presentation/helpers/open-work-details'
import { useRoutingContext } from '../../hooks/use-routing-context'
import { validateDuration } from '../../helpers'

const WorkSelectionTable: React.FC = () => {
  const {
    selectedWorks, setSelectedWorks, workDurations, setWorkDurations, durationErrors, setDurationErrors, selectionError
  } = useRoutingContext()
  const [options, setOptions] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<Work | null>(null)

  const handleDurationChange = (activityId: string, newTime: Dayjs | null) => {
    const { duration, error } = validateDuration(newTime)
    setWorkDurations(prev => ({ ...prev, [activityId]: duration }))
    setDurationErrors(prev => ({ ...prev, [activityId]: error }))
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>, row: Work) => {
    setOptions(event.currentTarget)
    setSelectedRow(row)
  }

  const handleClose = () => {
    setOptions(null)
  }
  
  const handleRemoveWork = (workId: number) => {
    setSelectedWorks(prev => prev.filter(work => work.id !== workId))
    setWorkDurations(prev => {
      const newDurations = { ...prev }
      delete newDurations[workId]
      return newDurations
    })
    setDurationErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[workId]
      return newErrors
    })
    handleClose()
  }

  const renderHeader = (e: GridColumnHeaderParams<any, any, any>) => <FormLabel>{e.colDef.headerName}</FormLabel>

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Título',
      flex: 1,
      renderHeader,
    },
    {
      field: 'resourcePlace',
      headerName: 'Cliente ou Local',
      flex: 1,
      renderHeader,
    },
    {
      field: 'address',
      headerName: 'Endereço',
      flex: 1,
      renderHeader,
    },
    {
      field: 'duration',
      headerName: 'Duração (hh:mm)',
      renderHeader: (e) => (
        <FormControl required>
          <FormLabel>
            {e.colDef.headerName}
          </FormLabel>
        </FormControl>
      ),
      flex: 1,
      renderCell: (params) => {
        const error = durationErrors[params.row.id]
        return (
          <Box sx={{ width: '100%', py: 1 }}>
            <TimeField
              value={workDurations[params.row.id] ?? null}
              onChange={(newValue) => handleDurationChange(params.row.id, newValue)}
              format="HH:mm"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: Boolean(error),
                  helperText: error,
                  onKeyDown: (e) => e.stopPropagation(),
                  InputProps: {
                    sx: {
                      fontSize: '0.875rem',
                    }
                  },
                  FormHelperTextProps: {
                    sx: {
                      marginLeft: 0,
                      marginTop: '2px',
                      fontSize: '0.75rem',
                    }
                  }
                }
              }}
            />
          </Box>
        )
      },
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 80,
      sortable: false,
      filterable: false,
      renderHeader,
      renderCell: (params) => (
        <IconButton
          aria-label="more"
          id="long-button"
          aria-haspopup="true"
          onClick={(e) => handleClick(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ]

  const rows = useMemo(() => selectedWorks.map((work) => ({
    id: work.id,
    title: work.title,
    resourcePlace: work.resourcePlace?.name,
    address: work.resourcePlace?.address,
  })), [selectedWorks])
  
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 })

  return (
    <>
    {selectedWorks.length > 0 &&
      <>
        <SelectionDataGrid
          columns={columns}
          rows={rows}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          getRowClassName={(params) => {
            const hasError = selectionError?.references?.includes(params.row.id.toString())
            return hasError ? 'error-row' : ''
          }}
          sx={{
            '& .error-row': {
              color: 'error.main',
            },
          }}
        />
        <Menu
          anchorEl={options}
          open={Boolean(options)}
          onClose={handleClose}
          slotProps={{
            list: {
              'aria-labelledby': 'basic-button',
            },
          }}
        >
          <MenuItem
            onClick={() => selectedRow && handleRemoveWork(selectedRow.id)}
          >
            <ListItemIcon>
              <CloseFilledIcon fontSize="medium" />
            </ListItemIcon>
            <Typography variant="body1">Remover</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose()
              selectedRow && openWorkDetails(selectedRow.id)
            }}
          >
            <ListItemIcon>
              <VisibilityRoundedIcon fontSize="medium" />
            </ListItemIcon>
            <Typography variant="body1">Ver detalhes</Typography>
          </MenuItem>
        </Menu>
      </>
      }
    </>
  )
}

export default WorkSelectionTable
